/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useMemo} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {FloatingIconButtonGroup} from '@ion/dls/src/components/buttons/FloatingIconButton';
import {MapZoomInIcon} from '@ion/dls/src/icons/MapZoomInIcon';
import {MapZoomOutIcon} from '@ion/dls/src/icons/MapZoomOutIcon';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import {IconSize} from '@ion/dls/src/types/types';
import Tooltip from '../../components/tooltip/Tooltip';
import {useMapFloatingIconButtonClass} from './mapFloatingButtonStyles';

// defines the prop definitions for the ZoomInOutTool Tool
export type ZoomInOutProps = {
  onZoomOut: React.MouseEventHandler;
  onZoomIn: React.MouseEventHandler;
  isVertical?: boolean;
  // overrides the default horizontal-layout marginBottom (ION-7522, for a
  // consumer whose own layout already provides spacing between rows)...
  style?: React.CSSProperties;
};

// cancels the default horizontal-layout marginBottom, for a caller whose
// own layout (e.g. a widget cluster's own row gap) already provides
// spacing - shared by TrackingWidgetCluster and GeofenceWidgetCluster so
// both clusters render with the same spacing between the zoom stepper and
// the tool next to it...
export const noZoomToolSpacing = {};

/**
 * @ZoomInOutTool
 *
 * The ZoomInOutTool component provides the UI which displays the associated Icon
 * button within a DLS Map that, when clicked, initiates the corresponding
 * feature.
 *
 * This ZoomInOutTool represents the "Zoom In and Zoom Out Tool" DLS features:
 * https://www.figma.com/file/srjlHrDuM90sCoaOwpjaDU/01_DLS_Components?node-id=1526%3A7422
 *
 * Button order is theme-dependent: the current theme renders Zoom Out
 * before Zoom In, while the legacy theme preserves the original Zoom In
 * before Zoom Out order.
 *
 * ```typescript
 *
 *   <ZoomInOutTool onZoomOut={function() {}}
 *     onZoomIn={function() {}}
 *     style={{marginBottom: 0}} />
 *
 * ```
 */
export const ZoomInOutTool = ({onZoomIn, onZoomOut, isVertical = true, style: styleProp}: ZoomInOutProps) => {
  const {isCurrent} = useThemeContext();
  // the stepper is grouped only when laid out horizontally ...
  const className   = useMapFloatingIconButtonClass(!isVertical);
  // the legacy horizontal stepper keeps its two buttons 3px apart, as before ...
  const style       = useMemo(() => (
    styleProp ?? (!isVertical ? {
      marginBottom : '8px',
      ...(!isCurrent && {gap: '3px'})
    } : {})
  ), [isCurrent, isVertical, styleProp]);

  const zoomOutButton = (
    <FloatingIconButtonGroup.FloatingIconButton onClick={onZoomOut}
      className={className}
      tooltipLabel={translate('maps:controls.zoomOut')}>
      <MapZoomOutIcon size={IconSize.medium} />
    </FloatingIconButtonGroup.FloatingIconButton>
  );

  const zoomInButton = (
    <FloatingIconButtonGroup.FloatingIconButton onClick={onZoomIn}
      className={className}
      tooltipLabel={translate('maps:controls.zoomIn')}>
      <MapZoomInIcon size={IconSize.medium} />
    </FloatingIconButtonGroup.FloatingIconButton>
  );

  // current theme orders Zoom Out before Zoom In (matching Admin Geofences'
  // design) - the legacy theme keeps its original markup untouched: Zoom In
  // then Zoom Out, each its own separate button under its own help text ...
  return (
    <FloatingIconButtonGroup isVertical={isVertical} style={style}>
      {isCurrent ? (
        <>
          {zoomOutButton}
          {zoomInButton}
        </>
      ) : (
        <>
          <Tooltip title={translate('maps:controls.zoomIn')}
            placement='left'
            arrow>
            <div>
              <FloatingIconButtonGroup.FloatingIconButton onClick={onZoomIn}>
                <MapZoomInIcon size={IconSize.medium} />
              </FloatingIconButtonGroup.FloatingIconButton>
            </div>
          </Tooltip>
          <Tooltip title={translate('maps:controls.zoomOut')}
            placement='left'
            arrow>
            <div>
              <FloatingIconButtonGroup.FloatingIconButton onClick={onZoomOut}>
                <MapZoomOutIcon size={IconSize.medium} />
              </FloatingIconButtonGroup.FloatingIconButton>
            </div>
          </Tooltip>
        </>
      )}
    </FloatingIconButtonGroup>
  );
};
export default memo(ZoomInOutTool);
